import React, { useEffect, useMemo, useState } from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';

const MARKUP_COLORS = ['#00e5ff', '#66ff66', '#ffeb3b', '#ff9800', '#ff5252', '#e040fb', '#ffffff'];
const MAX_MARKUP_TEXT_LENGTH = 160;

const annotationTitle = (item) => {
  const titles = {
    angle: 'Angle',
    arrow: 'Arrow',
    cobb: 'Cobb angle',
    ellipse: 'Ellipse',
    freehand: 'Freehand annotation',
    level: 'Vertebral level',
    rect: 'Rectangle',
    text: 'Text annotation',
  };
  return titles[item?.type] || 'Annotation';
};

const targetTitle = (target) => {
  if (target?.kind === 'measurement') return 'Measurement';
  if (target?.kind === 'roi') return 'ROI';
  return annotationTitle(target?.item);
};

const clampAnchor = (value, viewportSize, panelSize) => Math.max(8, Math.min(value, Math.max(8, viewportSize - panelSize - 8)));
const sanitizeMarkupText = (value) => Array.from(String(value || ''))
  .filter(character => {
    const code = character.charCodeAt(0);
    return code >= 32 && code !== 127;
  })
  .join('')
  .slice(0, MAX_MARKUP_TEXT_LENGTH);

const controlLabelStyle = {
  color: '#9fb6bd',
  fontSize: 9,
  fontFamily: 'monospace',
  letterSpacing: '0.08em',
};

const draftForTarget = (target) => {
  const item = target?.item || {};
  const calculatedLabel = target?.kind === 'measurement'
    || target?.kind === 'roi'
    || (target?.kind === 'annotation' && ['angle', 'cobb'].includes(item.type));
  const textField = target?.kind === 'measurement'
    ? { label: 'Label text', key: 'label', value: item.label || '' }
    : target?.kind === 'annotation' && (item.type === 'text' || item.type === 'level')
      ? { label: 'Annotation text', key: 'text', value: item.text || '' }
      : null;
  return {
    calculatedLabel,
    textField,
    lineWidth: Number(item.lineWidth) || 2,
    textSize: Number(calculatedLabel ? item.labelFontSize : item.fontSize) || 12,
    color: item.color || '#00e5ff',
    text: sanitizeMarkupText(textField?.value || ''),
  };
};

const ClinicalMarkupPropertiesPopover = ({ anchor, target, onPreview, onApply, onCancel, onDelete }) => {
  const initialDraft = useMemo(() => draftForTarget(target), [target]);
  const [draft, setDraft] = useState(initialDraft);
  const [changes, setChanges] = useState({});

  useEffect(() => {
    setDraft(initialDraft);
    setChanges({});
  }, [initialDraft]);

  if (!anchor || !target?.item) return null;
  const title = targetTitle(target);
  const viewportWidth = typeof window === 'undefined' ? 1280 : window.innerWidth;
  const viewportHeight = typeof window === 'undefined' ? 800 : window.innerHeight;
  const panelWidth = 236;
  const panelHeight = 300;
  const preferredLeft = anchor.x + 16 + panelWidth <= viewportWidth - 8
    ? anchor.x + 16
    : anchor.x - panelWidth - 16;
  const left = clampAnchor(preferredLeft, viewportWidth, panelWidth);
  const top = clampAnchor(anchor.y + 12, viewportHeight, panelHeight);
  const hasTextSize = draft.calculatedLabel || Boolean(draft.textField);

  const updateDraft = (key, value, patchKey = key) => {
    setDraft(current => ({ ...current, [key]: value }));
    setChanges(current => ({ ...current, [patchKey]: value }));
    onPreview?.({ [patchKey]: value });
  };

  const applyAndClose = () => {
    onApply?.(changes);
  };

  return (
    <Box
      role="dialog"
      aria-label={`${title} properties`}
      data-kairos-marker="KAIROS_CLINICAL_MARKUP_PROPERTIES"
      onClick={(event) => event.stopPropagation()}
      onPointerDown={(event) => event.stopPropagation()}
      onContextMenu={(event) => { event.preventDefault(); event.stopPropagation(); }}
      onKeyDown={(event) => { if (event.key === 'Escape') onCancel?.(); }}
      sx={{
        position: 'fixed',
        left,
        top,
        zIndex: 150,
        width: panelWidth,
        p: 1,
        color: '#d8fbff',
        bgcolor: '#050c0f',
        border: '1px solid rgba(110, 239, 255, 0.42)',
        borderRadius: '7px',
        boxShadow: '0 10px 32px rgba(0,0,0,0.72), 0 0 18px rgba(0,229,255,0.08)',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.8 }}>
        <Typography sx={{ color: '#6eefff', fontSize: 10, fontWeight: 800, fontFamily: 'monospace', letterSpacing: '0.1em' }}>
          {title.toUpperCase()} PROPERTIES
        </Typography>
        <ButtonBase aria-label={`Close ${title} properties without applying`} onClick={onCancel} sx={{ color: '#78909c', fontSize: 14, px: 0.5, '&:hover': { color: '#d8fbff' } }}>×</ButtonBase>
      </Box>

      {draft.textField && (
        <Box sx={{ mb: 0.9 }}>
          <Typography component="label" htmlFor="clinical-markup-property-text" sx={controlLabelStyle}>{draft.textField.label.toUpperCase()}</Typography>
          <input
            id="clinical-markup-property-text"
            aria-label={draft.textField.label}
            maxLength={MAX_MARKUP_TEXT_LENGTH}
            value={draft.text}
            onChange={(event) => updateDraft('text', sanitizeMarkupText(event.target.value), draft.textField.key)}
            style={{ width: '100%', marginTop: 3, boxSizing: 'border-box', background: '#071014', border: '1px solid #263f46', borderRadius: 4, color: '#e7fbff', padding: '5px 7px', fontSize: 11 }}
          />
        </Box>
      )}

      <Box sx={{ mb: 0.8 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography component="label" htmlFor="clinical-markup-line-width" sx={controlLabelStyle}>LINE THICKNESS</Typography>
          <Typography aria-live="polite" sx={{ ...controlLabelStyle, color: '#d8fbff' }}>{draft.lineWidth}px</Typography>
        </Box>
        <input
          id="clinical-markup-line-width"
          aria-label="Line thickness"
          type="range"
          min="1"
          max="6"
          step="1"
          value={draft.lineWidth}
          onChange={(event) => updateDraft('lineWidth', Number(event.target.value))}
          style={{ width: '100%', accentColor: '#00e5ff' }}
        />
      </Box>

      {hasTextSize && (
        <Box sx={{ mb: 0.9 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography component="label" htmlFor="clinical-markup-font-size" sx={controlLabelStyle}>TEXT SIZE</Typography>
            <Typography aria-live="polite" sx={{ ...controlLabelStyle, color: '#d8fbff' }}>{draft.textSize}px</Typography>
          </Box>
          <input
            id="clinical-markup-font-size"
            aria-label="Text size"
            type="range"
            min="8"
            max="24"
            step="1"
            value={draft.textSize}
            onChange={(event) => updateDraft('textSize', Number(event.target.value), draft.calculatedLabel ? 'labelFontSize' : 'fontSize')}
            style={{ width: '100%', accentColor: '#00e5ff' }}
          />
        </Box>
      )}

      <Typography sx={{ ...controlLabelStyle, mb: 0.45 }}>COLOR</Typography>
      <Box sx={{ display: 'flex', gap: 0.55, mb: 1 }}>
        {MARKUP_COLORS.map(color => (
          <ButtonBase
            key={color}
            aria-label={`Use annotation color ${color}`}
            aria-pressed={draft.color.toLowerCase() === color}
            onClick={() => updateDraft('color', color)}
            sx={{ width: 19, height: 19, borderRadius: '50%', bgcolor: color, border: draft.color.toLowerCase() === color ? '2px solid #fff' : '2px solid rgba(255,255,255,0.08)', boxShadow: '0 0 0 1px rgba(0,0,0,0.8)' }}
          />
        ))}
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid rgba(110,239,255,0.12)', pt: 0.7 }}>
        <ButtonBase aria-label={`Delete ${title}`} onClick={onDelete} sx={{ color: '#ff7b7b', fontSize: 10, fontFamily: 'monospace', px: 0.4, '&:hover': { color: '#ffb0b0' } }}>DELETE</ButtonBase>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <ButtonBase aria-label={`Cancel ${title} property changes`} onClick={onCancel} sx={{ color: '#9fb6bd', fontSize: 10, fontFamily: 'monospace', px: 0.4 }}>CANCEL</ButtonBase>
          <ButtonBase aria-label={`Apply ${title} properties`} onClick={applyAndClose} sx={{ color: '#6eefff', fontSize: 10, fontFamily: 'monospace', px: 0.4 }}>APPLY</ButtonBase>
        </Box>
      </Box>
    </Box>
  );
};

export default ClinicalMarkupPropertiesPopover;
